import React, { useState } from "react";
import { UserCheck, CircleCheck } from "lucide-react";
import { Card } from "@/components/ui";
import { QuickCreateFanForm } from "../QuickCreateFanForm";
import { useShareFormLink } from "../../context/ShareFormLinkContext";

export function StepChooseFan() {
  const { setCreatedFan, fans } = useShareFormLink();

  // local selected fan option state
  const [fanSource, setFanSource] = useState<"generic" | "personalized">("personalized");

  const selectGeneric = () => {
    setFanSource("generic");
    setCreatedFan(null);
    fans.onValueChange(null as any, undefined);
  };

  return (
    <div className="flex flex-col gap-4 animate-in fade-in duration-300">
      <div className="flex flex-col gap-1.5">
        <div className="font-bold text-slate-800 text-lg">Step 1: Choose Recipient</div>
        <div className="text-sm text-slate-500 font-medium leading-relaxed">
          Decide whether to generate a reusable link or personalize it for someone specific.
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3.5">
        {/* Option A: Specific Fan */}
        <Card
          onClick={() => setFanSource("personalized")}
          className={`p-4 border rounded-xl cursor-pointer flex flex-col gap-4 transition-all ${
            fanSource === "personalized"
              ? "border-primary bg-primary/5 shadow-sm"
              : "border-slate-200 bg-white hover:bg-slate-50"
          }`}
        >
          <div className="flex items-center justify-between gap-4 w-full">
            <div className="flex items-center gap-3.5 min-w-0 flex-1">
              <div
                className={`p-3 rounded-xl border shrink-0 ${
                  fanSource === "personalized"
                    ? "border-primary/20 bg-primary/10 text-primary"
                    : "border-slate-200 bg-slate-50 text-slate-500"
                }`}
              >
                <UserCheck className="size-5" />
              </div>
              <div className="flex flex-col min-w-0 text-left">
                <span className="text-sm font-bold text-slate-800 leading-tight">Send to a specific fan</span>
                <span className="text-sm text-slate-500 font-medium leading-relaxed mt-1">
                  Personalize the link and automatically log their review back to their fan profile.
                </span>
              </div>
            </div>
            <div
              className={`size-6 rounded-full border flex items-center justify-center shrink-0 ml-auto ${
                fanSource === "personalized" ? "border-primary bg-primary text-white" : "border-slate-300"
              }`}
            >
              {fanSource === "personalized" && <CircleCheck className="size-4 stroke-[3]" />}
            </div>
          </div>

          {fanSource === "personalized" && (
            <div
              className="flex flex-col gap-4 w-full animate-in slide-in-from-top-1 duration-200"
              onClick={e => e.stopPropagation()}
            >
              <QuickCreateFanForm />
            </div>
          )}
        </Card>

        {/* Option B: Generic Link */}
        <Card
          onClick={selectGeneric}
          className={`p-4 border rounded-xl cursor-pointer flex flex-row items-center justify-between gap-4 transition-all ${
            fanSource === "generic"
              ? "border-primary bg-primary/5 shadow-sm"
              : "border-slate-200 bg-white hover:bg-slate-50"
          }`}
        >
          <div className="flex items-center gap-3.5 min-w-0 flex-1">
            <div
              className={`p-3 rounded-xl border shrink-0 ${
                fanSource === "generic"
                  ? "border-primary/20 bg-primary/10 text-primary"
                  : "border-slate-200 bg-slate-50 text-slate-500"
              }`}
            >
              <UserCheck className="size-5" />
            </div>
            <div className="flex flex-col min-w-0 text-left">
              <span className="text-sm font-bold text-slate-800 leading-tight">Send to anyone (Generic Link)</span>
              <span className="text-sm text-slate-500 font-medium leading-relaxed mt-1">
                A reusable link that can be shared with multiple people. It isn't tied to any single fan's profile.
              </span>
            </div>
          </div>
          <div
            className={`size-6 rounded-full border flex items-center justify-center shrink-0 ml-auto ${
              fanSource === "generic" ? "border-primary bg-primary text-white" : "border-slate-300"
            }`}
          >
            {fanSource === "generic" && <CircleCheck className="size-4 stroke-[3]" />}
          </div>
        </Card>
      </div>
    </div>
  );
}
